import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search,
  Download,
  CheckCircle,
  X,
  AccountBalanceWallet,
  Eye,
  FilterList,
  CalendarToday,
  Activity,
  Navigation,
  User,
  Globe,
  Heart,
  Award,
  Route,
  Trophy,
  MapPin,
  Gift,
  ArrowUp,
  Clock,
  RotateCw,
} from 'lucide-react';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { API_URL } from './config';

const WithdrawalManagementPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [withdrawalsData, setWithdrawalsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
  }, [statusFilter, dateRange]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching withdrawal requests...');
      
      let apiUrl = `${API_URL}/withdrawal/requests`;
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (dateRange.start) {
        params.append('startDate', dateRange.start.toISOString());
      }
      
      if (dateRange.end) {
        params.append('endDate', dateRange.end.toISOString());
      }
      
      if (params.toString()) {
        apiUrl += `?${params.toString()}`;
      }
      
      console.log('📡 API URL:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📊 API Response data:', data);
      
      if (data.status === 200) {
        console.log('✅ Success! Withdrawals data:', data.withdrawals);
        setWithdrawalsData(data.withdrawals || []);
      } else {
        throw new Error(`API returned status: ${data.status}`);
      }
    } catch (error) {
      console.error('❌ Error fetching withdrawals:', error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Failed to fetch withdrawal requests. Please try again.'
      });
      // Set demo data for development
      setWithdrawalsData(getDemoWithdrawalData());
    } finally {
      setLoading(false);
    }
  };

  const getDemoWithdrawalData = () => {
    return [
      {
        id: 1,
        driver_id: 'DRV001',
        driver_name: 'John Smith',
        amount: 150.00,
        method: 'bank_transfer',
        status: 'pending',
        requested_at: '2026-02-02T10:30:00Z',
        processed_at: null,
        processed_by: null,
        payout_details: {
          bank_name: 'Standard Bank',
          account_number: '****1234',
          account_holder: 'John Smith'
        }
      },
      {
        id: 2,
        driver_id: 'DRV002',
        driver_name: 'Mary Johnson',
        amount: 85.50,
        method: 'mobile_money',
        status: 'processing',
        requested_at: '2026-02-02T09:15:00Z',
        processed_at: '2026-02-02T11:00:00Z',
        processed_by: 'admin',
        payout_details: {
          mobile_number: '+263712345678',
          provider: 'EcoCash'
        }
      },
      {
        id: 3,
        driver_id: 'DRV003',
        driver_name: 'Robert Chen',
        amount: 200.00,
        method: 'bank_transfer',
        status: 'completed',
        requested_at: '2026-02-01T14:20:00Z',
        processed_at: '2026-02-01T16:30:00Z',
        processed_by: 'admin',
        payout_details: {
          bank_name: 'CBZ Bank',
          account_number: '****5678',
          account_holder: 'Robert Chen'
        }
      }
    ];
  };

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    try {
      console.log(`🔄 Processing withdrawal ${withdrawalId} with action: ${action}`);
      
      const response = await fetch(`${API_URL}/withdrawal/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          withdrawal_id: withdrawalId,
          action: action, // 'approve' or 'reject'
          processed_by: 'admin' // This should come from authentication
        })
      });

      const data = await response.json();
      
      if (data.status === 200) {
        setAlert({
          show: true,
          type: 'success',
          message: `Withdrawal ${action === 'approve' ? 'approved' : 'rejected'} successfully!`
        });
        fetchWithdrawals(); // Refresh data
      } else {
        throw new Error(data.message || 'Failed to process withdrawal');
      }
    } catch (error) {
      console.error('❌ Error processing withdrawal:', error);
      setAlert({
        show: true,
        type: 'error',
        message: `Failed to ${action} withdrawal: ${error.message}`
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock />;
      case 'processing': return <RotateCw />;
      case 'completed': return <CheckCircle />;
      case 'failed': return <X />;
      case 'cancelled': return <X />;
      default: return <Clock />;
    }
  };

  const filteredWithdrawals = withdrawalsData.filter(withdrawal => {
    const matchesSearch = withdrawal.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         withdrawal.driver_id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleViewDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setDetailsDialogOpen(true);
  };

  const exportData = () => {
    const csvContent = [
      ['ID', 'Driver ID', 'Driver Name', 'Amount', 'Method', 'Status', 'Requested At', 'Processed At', 'Processed By'],
      ...filteredWithdrawals.map(w => [
        w.id,
        w.driver_id,
        w.driver_name,
        w.amount,
        w.method,
        w.status,
        w.requested_at,
        w.processed_at || '',
        w.processed_by || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `withdrawal_requests_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header with Instructions */}
        <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.900' }}>
            💳 Withdrawal Management
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            Process driver withdrawal requests efficiently and securely.
          </Typography>
          
          {/* Quick Instructions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip 
              icon={<CheckCircle />} 
              label="Review Pending Requests" 
              color="primary" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              icon={<Eye />} 
              label="View Details Before Action" 
              color="info" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              icon={<TrendingUpIcon />} 
              label="Approve Valid Withdrawals" 
              color="success" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              icon={<X />} 
              label="Reject Suspicious Requests" 
              color="error" 
              variant="outlined" 
              size="small" 
            />
          </Box>
        </Box>

        {/* Status Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="warning.dark">
                  {withdrawalsData.filter(w => w.status === 'pending').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ⏳ Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="info.dark">
                  {withdrawalsData.filter(w => w.status === 'processing').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  🔄 Processing
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="success.dark">
                  {withdrawalsData.filter(w => w.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ✅ Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="error.dark">
                  ${withdrawalsData.filter(w => w.status === 'pending').reduce((sum, w) => sum + (w.amount || 0), 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  💰 Pending Amount
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 2 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="🔍 Search driver..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
              }}
              helperText="Search by driver name or ID"
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>📊 Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">📋 All Status</MenuItem>
                <MenuItem value="pending">⏳ Pending</MenuItem>
                <MenuItem value="processing">🔄 Processing</MenuItem>
                <MenuItem value="completed">✅ Completed</MenuItem>
                <MenuItem value="failed">❌ Failed</MenuItem>
                <MenuItem value="cancelled">🚫 Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <DatePicker
              label="📅 Start Date"
              value={dateRange.start}
              onChange={(newValue) => setDateRange({ ...dateRange, start: newValue })}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <DatePicker
              label="📅 End Date"
              value={dateRange.end}
              onChange={(newValue) => setDateRange({ ...dateRange, end: newValue })}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<RotateCw />}
                onClick={fetchWithdrawals}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                🔄 Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportData}
                disabled={filteredWithdrawals.length === 0}
                sx={{ flex: 1 }}
              >
                📥 Export
              </Button>
            </Box>
          </Grid>
        </Grid>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                📋 Withdrawal Requests ({filteredWithdrawals.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                💡 Click actions to approve/reject pending withdrawals
              </Typography>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>🆔 ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>👤 Driver</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>💰 Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>💳 Method</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>📊 Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>📅 Requested</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>⚡ Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWithdrawals.map((withdrawal) => (
                    <TableRow 
                      key={withdrawal.id}
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: withdrawal.status === 'pending' ? 'warning.50' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          #{withdrawal.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            👤 {withdrawal.driver_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            🆔 {withdrawal.driver_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          💵 ${withdrawal.amount?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          💳 {withdrawal.method?.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(withdrawal.status)}
                          label={withdrawal.status?.toUpperCase()}
                          color={getStatusColor(withdrawal.status)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            📅 {new Date(withdrawal.requested_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            🕐 {new Date(withdrawal.requested_at).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <Tooltip title="🔍 View full withdrawal details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(withdrawal)}
                              sx={{ bgcolor: 'info.50', '&:hover': { bgcolor: 'info.100' } }}
                            >
                              <Eye fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {withdrawal.status === 'pending' && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="✅ Approve this withdrawal">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleProcessWithdrawal(withdrawal.id, 'approve')}
                                  sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="❌ Reject this withdrawal">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleProcessWithdrawal(withdrawal.id, 'reject')}
                                  sx={{ bgcolor: 'error.50', '&:hover': { bgcolor: 'error.100' } }}
                                >
                                  <X fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          
                          {withdrawal.status !== 'pending' && (
                            <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center' }}>
                              {withdrawal.status === 'completed' ? '✅ Done' : 
                               withdrawal.status === 'processing' ? '🔄 In Progress' : 
                               withdrawal.status === 'cancelled' ? '🚫 Cancelled' : '❌ Failed'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {filteredWithdrawals.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  📭 No withdrawal requests found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Try adjusting your filters or check back later for new requests.
                </Typography>
                <Button variant="outlined" onClick={fetchWithdrawals} startIcon={<RotateCw />}>
                  🔄 Refresh Data
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.50', color: 'primary.900' }}>
            💳 Withdrawal Details - #{selectedWithdrawal?.id}
          </DialogTitle>
          <DialogContent>
            {selectedWithdrawal && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      👤 Driver Information
                    </Typography>
                    <Typography variant="body2">👤 Name: {selectedWithdrawal.driver_name}</Typography>
                    <Typography variant="body2">🆔 ID: {selectedWithdrawal.driver_id}</Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      💰 Withdrawal Details
                    </Typography>
                    <Typography variant="body2">💵 Amount: ${selectedWithdrawal.amount?.toFixed(2)}</Typography>
                    <Typography variant="body2">💳 Method: {selectedWithdrawal.method?.replace('_', ' ').toUpperCase()}</Typography>
                    <Typography variant="body2">📊 Status: {selectedWithdrawal.status?.toUpperCase()}</Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      ⏰ Timeline
                    </Typography>
                    <Typography variant="body2">📅 Requested: {new Date(selectedWithdrawal.requested_at).toLocaleString()}</Typography>
                    {selectedWithdrawal.processed_at && (
                      <Typography variant="body2">✅ Processed: {new Date(selectedWithdrawal.processed_at).toLocaleString()}</Typography>
                    )}
                    {selectedWithdrawal.processed_by && (
                      <Typography variant="body2">👤 Processed By: {selectedWithdrawal.processed_by}</Typography>
                    )}
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🏦 Payout Details
                    </Typography>
                    {selectedWithdrawal.payout_details && Object.entries(selectedWithdrawal.payout_details).map(([key, value]) => (
                      <Typography variant="body2" key={key}>
                        📋 {key.replace('_', ' ').toUpperCase()}: {value}
                      </Typography>
                    ))}
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined">
              ❌ Close
            </Button>
            {selectedWithdrawal?.status === 'pending' && (
              <>
                <Button 
                  color="success" 
                  onClick={() => {
                    handleProcessWithdrawal(selectedWithdrawal.id, 'approve');
                    setDetailsDialogOpen(false);
                  }}
                  variant="contained"
                  startIcon={<CheckCircle />}
                >
                  ✅ Approve
                </Button>
                <Button 
                  color="error" 
                  onClick={() => {
                    handleProcessWithdrawal(selectedWithdrawal.id, 'reject');
                    setDetailsDialogOpen(false);
                  }}
                  variant="contained"
                  startIcon={<X />}
                >
                  ❌ Reject
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default WithdrawalManagementPanel;
